import { IconButton, Tooltip } from "@chakra-ui/react";
import { Zap } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAppData, useConfigGuard } from "../../contexts/AppContext";
import { canAccessAutomacoes } from "../../routes/routeAccess";
import { CADIN_FEDERAL_SLUG, CONTA_CORRENTE_SUBPAGINAS } from "../../pages/TaxCurrentAccount/contaCorrenteSlugs";
import { checkCollaboratorPermission } from "../../pages/Users/utils/checkPermission";
import { TOPNAV_ICON_BUTTON_PROPS } from "../TopNav/constants";

/**
 * Módulos que filtram o atalho de Automações da barra superior.
 *
 * A chave é o `moduleKey` que o backend DERIVA do gatilho (`moduleOfTrigger`, pacote
 * `@taxlybr/automations`) — é ela que a lista de Automações usa para filtrar. Uma chave diferente
 * aqui abriria a tela filtrada num módulo sem regra nenhuma.
 *  - `dte`: mensagem recebida / marcada com tag (Caixa Postal)
 *  - `certificate`: status de certidão mudou (Certidões)
 *  - `status`: situação mudou — Conta Corrente, dívida ativa, CNPJ, inscrição estadual
 */
export type ModuloDeAutomacao = "dte" | "certificate" | "status";

/**
 * Pode ver o atalho? O MESMO par da sidebar e da rota `/automacoes`: Automações liberado e ligado para o
 * cliente (`canAccessAutomacoes` — GEN-4080 r4: o Kanban 2.0 não abre mais o módulo) + permissão de
 * leitura do módulo.
 *
 * ⚠️ Sem o gate, quem tem o módulo e não tem Automações via o botão, clicava, e o `RequirePermission`
 * devolvia para a home em silêncio. Atalho que leva a lugar nenhum é pior que atalho ausente.
 *
 * Exportado também para os testes.
 */
export function usePodeVerAutomacoes(): boolean {
  const appData = useAppData();
  const guid = appData.currentClient?.guid_client as string | undefined;
  return !!(
    guid &&
    canAccessAutomacoes(appData) &&
    checkCollaboratorPermission(appData.validation, guid, "automations.read")
  );
}

interface AtalhoDaPagina {
  /** Destino na SPA (`/automacoes` com ou sem filtro). */
  destino: string;
  /** Tooltip e nome acessível — diz de QUAL módulo são as regras. */
  rotulo: string;
}

/**
 * Para onde o raio da barra leva, a partir da página em que a pessoa está.
 *
 * Decisão do Thiago (14/10/2026): o atalho de Automações mora na barra superior (não dentro de cada
 * módulo) e abre a tela já filtrada pelo contexto da página. Fora dos módulos com automações, abre
 * todas as regras.
 *  - Certidões → `?modulo=certificate`
 *  - Caixa Postal → `?modulo=dte`
 *  - Conta Corrente → `?modulo=status&assunto=tax_current_account` (decisão de 02/10: só as regras de
 *    Conta Corrente — `status` sozinho traria dívida ativa, CNPJ, IE e certidões). A sub-página do
 *    CADIN Federal (GEN-3984) não é esse assunto: cai em todas.
 */
export function atalhoDeAutomacoesDaPagina(pathname: string): AtalhoDaPagina {
  const rota = (pathname || "/").replace(/\/+$/, "") || "/";
  const filtrado = (modulo: ModuloDeAutomacao, rotulo: string, assunto?: string): AtalhoDaPagina => ({
    destino: `/automacoes?modulo=${modulo}${assunto ? `&assunto=${encodeURIComponent(assunto)}` : ""}`,
    rotulo,
  });
  if (rota === "/certidoes" || rota.startsWith("/certidoes/")) return filtrado("certificate", "Automações de Certidões");
  if (rota === "/caixa-postal" || rota.startsWith("/caixa-postal/")) return filtrado("dte", "Automações da Caixa Postal");
  // Codex #2877: a rota do CADIN usa o `path` do registro de sub-páginas ("cadin-federal"), não o slug.
  const rotaDoCadin = `/conta-corrente/${CONTA_CORRENTE_SUBPAGINAS.find((sp) => sp.slug === CADIN_FEDERAL_SLUG)?.path}`;
  const naSubpaginaDoCadin = rota === rotaDoCadin || rota.startsWith(`${rotaDoCadin}/`);
  if ((rota === "/conta-corrente" || rota.startsWith("/conta-corrente/")) && !naSubpaginaDoCadin) {
    return filtrado("status", "Automações de Conta Corrente", "tax_current_account");
  }
  return { destino: "/automacoes", rotulo: "Automações" };
}

/**
 * Raio de Automações da barra superior (GEN-3471, 14/10/2026). Substitui os botões que moravam na linha
 * de apoio do cabeçalho de Certidões, Caixa Postal e Conta Corrente.
 *
 * Gate: o mesmo da sidebar e da rota `/automacoes` (`usePodeVerAutomacoes`) — atalho que leva a lugar
 * nenhum é pior que atalho ausente. Navega pela SPA (`requestNavigation`, como os outros atalhos da
 * barra): recarregar descartaria o cliente ativo, que vive só em memória.
 */
export const AutomacoesNaBarra = () => {
  const { pathname } = useLocation();
  const { requestNavigation } = useConfigGuard();
  const pode = usePodeVerAutomacoes();
  if (!pode) return null;
  const { destino, rotulo } = atalhoDeAutomacoesDaPagina(pathname);
  return (
    <Tooltip label={rotulo} openDelay={600}>
      <IconButton
        aria-label={rotulo}
        {...TOPNAV_ICON_BUTTON_PROPS}
        icon={<Zap size={17} />}
        onClick={() => requestNavigation(destino)}
      />
    </Tooltip>
  );
};
